/** @typedef {{ field: string, operator: string, value?: unknown }} GridFilterItem */
/** @typedef {{ field: string, sort: 'asc' | 'desc' }} GridSortItem */

/** @type {Set<string>} */
const VALID_SORT = new Set(["asc", "desc"]);

/** @type {Set<string>} */
const VALID_COLUMN_VISIBILITY = new Set(["hidden", "visible"]);

/** @type {Set<string>} */
const VALID_FILTER_OPERATORS = new Set([
  "contains",
  "doesNotContain",
  "equals",
  "doesNotEqual",
  "startsWith",
  "endsWith",
  "isEmpty",
  "isNotEmpty",
  "isAnyOf",
  "isNotAnyOf",
  "greaterThan",
  "greaterThanOrEqual",
  "lessThan",
  "lessThanOrEqual",
  "is",
  "not",
]);

const ARRAY_VALUE_OPERATORS = new Set(["isAnyOf", "isNotAnyOf"]);

/**
 * @param {string} urlStateKey
 * @param {...string} segments
 * @returns {string}
 */
export function buildPrefixedKey(urlStateKey, ...segments) {
  return [urlStateKey, ...segments].join(".");
}

/**
 * @param {string} urlStateKey
 * @returns {string}
 */
function prefixWithDot(urlStateKey) {
  return `${urlStateKey}.`;
}

/**
 * @param {string} value
 * @returns {string}
 */
export function escapeFilterValue(value) {
  return String(value).replace(/~/g, "~~");
}

/**
 * @param {string} value
 * @returns {string}
 */
export function unescapeFilterValue(value) {
  return value.replace(/~~/g, "~");
}

/**
 * @param {string} raw
 * @returns {{ value: string, operator: string } | null}
 */
export function splitFilterValueOperator(raw) {
  for (let index = raw.length - 1; index >= 0; index -= 1) {
    if (raw[index] !== "~") {
      continue;
    }
    if (index > 0 && raw[index - 1] === "~") {
      index -= 1;
      continue;
    }
    return {
      value: unescapeFilterValue(raw.slice(0, index)),
      operator: raw.slice(index + 1),
    };
  }
  return null;
}

/**
 * @param {string} field
 * @param {Set<string>|Array<string>|undefined} allowedFields
 * @returns {boolean}
 */
function isAllowedField(field, allowedFields) {
  if (!allowedFields) {
    return true;
  }
  const set = allowedFields instanceof Set ? allowedFields : new Set(allowedFields);
  return set.has(field);
}

/**
 * @param {string} field
 * @param {Record<string, boolean>|undefined} defaultColumnVisibility
 * @returns {boolean}
 */
export function isColumnVisibleByDefault(field, defaultColumnVisibility) {
  return defaultColumnVisibility?.[field] !== false;
}

/**
 * @param {string} field
 * @param {Record<string, boolean>|undefined} columnVisibilityModel
 * @returns {boolean}
 */
export function isColumnVisible(field, columnVisibilityModel) {
  return columnVisibilityModel?.[field] !== false;
}

/**
 * @param {URLSearchParams} searchParams
 * @param {string} urlStateKey
 * @returns {Generator<[string, string]>}
 */
function* iteratePrefixedParams(searchParams, urlStateKey) {
  const prefix = prefixWithDot(urlStateKey);
  for (const [key, value] of searchParams.entries()) {
    if (key.startsWith(prefix)) {
      yield [key, value];
    }
  }
}

/**
 * @param {URLSearchParams} searchParams
 * @param {string} urlStateKey
 * @param {Object} options
 * @param {number} options.defaultPageSize
 * @param {Record<string, boolean>} [options.defaultColumnVisibility]
 * @param {string[]} [options.allowedFields]
 * @returns {{
 *   filterItems: GridFilterItem[],
 *   quickFilterValue: string,
 *   sortModel: GridSortItem[],
 *   paginationModel: { page: number, pageSize: number },
 *   columnVisibilityModel: Record<string, boolean>,
 * }}
 */
export function parseGridUrlState(searchParams, urlStateKey, options) {
  const { defaultPageSize, defaultColumnVisibility = {}, allowedFields } = options;
  const allowedSet = allowedFields ? new Set(allowedFields) : undefined;

  /** @type {GridFilterItem[]} */
  const filterItems = [];
  /** @type {GridSortItem[]} */
  const sortModel = [];
  let quickFilterValue = "";
  let page = 0;
  let pageSize = defaultPageSize;
  /** @type {Record<string, boolean>} */
  const columnVisibilityModel = { ...defaultColumnVisibility };

  for (const [key, rawValue] of iteratePrefixedParams(searchParams, urlStateKey)) {
    const suffix = key.slice(prefixWithDot(urlStateKey).length);

    if (suffix.startsWith("filter.")) {
      const field = suffix.slice("filter.".length);
      if (!isAllowedField(field, allowedSet)) {
        continue;
      }
      const parsed = splitFilterValueOperator(rawValue);
      if (!parsed || !VALID_FILTER_OPERATORS.has(parsed.operator)) {
        continue;
      }
      let value = parsed.value;
      if (ARRAY_VALUE_OPERATORS.has(parsed.operator)) {
        value = value.length > 0 ? value.split(",") : [];
      }
      filterItems.push({ field, operator: parsed.operator, value });
      continue;
    }

    if (suffix.startsWith("sort.")) {
      const field = suffix.slice("sort.".length);
      if (!isAllowedField(field, allowedSet) || !VALID_SORT.has(rawValue)) {
        continue;
      }
      sortModel.push({ field, sort: /** @type {'asc' | 'desc'} */ (rawValue) });
      continue;
    }

    if (suffix.startsWith("col.")) {
      const field = suffix.slice("col.".length);
      if (!isAllowedField(field, allowedSet) || !VALID_COLUMN_VISIBILITY.has(rawValue)) {
        continue;
      }
      columnVisibilityModel[field] = rawValue === "visible";
      continue;
    }

    if (suffix === "q") {
      quickFilterValue = rawValue;
      continue;
    }

    if (suffix === "page") {
      const parsedPage = Number.parseInt(rawValue, 10);
      if (Number.isFinite(parsedPage) && parsedPage >= 0) {
        page = parsedPage;
      }
      continue;
    }

    if (suffix === "pageSize") {
      const parsedPageSize = Number.parseInt(rawValue, 10);
      if (Number.isFinite(parsedPageSize) && parsedPageSize > 0) {
        pageSize = parsedPageSize;
      }
    }
  }

  return {
    filterItems,
    quickFilterValue,
    sortModel,
    paginationModel: { page, pageSize },
    columnVisibilityModel,
  };
}

/**
 * @param {unknown} value
 * @param {string} operator
 * @returns {string}
 */
function serializeFilterValue(value, operator) {
  if (ARRAY_VALUE_OPERATORS.has(operator) && Array.isArray(value)) {
    return value.map(String).join(",");
  }
  if (value == null) {
    return "";
  }
  return String(value);
}

/**
 * @param {Object} state
 * @param {Object} state.filterModel
 * @param {GridSortItem[]} state.sortModel
 * @param {{ page: number, pageSize: number }} state.paginationModel
 * @param {Record<string, boolean>} state.columnVisibilityModel
 * @param {string} urlStateKey
 * @param {Object} options
 * @param {number} options.defaultPageSize
 * @param {Record<string, boolean>} [options.defaultColumnVisibility]
 * @param {string[]} [options.allowedFields]
 * @returns {Record<string, string>}
 */
export function serializeGridUrlState(state, urlStateKey, options) {
  const { defaultPageSize, defaultColumnVisibility = {}, allowedFields } = options;
  const allowedSet = allowedFields ? new Set(allowedFields) : undefined;
  /** @type {Record<string, string>} */
  const params = {};

  const quickFilterValue = state.filterModel?.quickFilterValues?.[0] ?? "";
  if (quickFilterValue) {
    params[buildPrefixedKey(urlStateKey, "q")] = quickFilterValue;
  }

  for (const item of state.filterModel?.items ?? []) {
    if (!item?.field || !item.operator) {
      continue;
    }
    if (!isAllowedField(item.field, allowedSet)) {
      continue;
    }
    if (!VALID_FILTER_OPERATORS.has(item.operator)) {
      continue;
    }
    const serializedValue = serializeFilterValue(item.value, item.operator);
    params[buildPrefixedKey(urlStateKey, "filter", item.field)] =
      `${escapeFilterValue(serializedValue)}~${item.operator}`;
  }

  for (const item of state.sortModel ?? []) {
    if (!item?.field || !VALID_SORT.has(item.sort)) {
      continue;
    }
    if (!isAllowedField(item.field, allowedSet)) {
      continue;
    }
    params[buildPrefixedKey(urlStateKey, "sort", item.field)] = item.sort;
  }

  const page = state.paginationModel?.page ?? 0;
  const pageSize = state.paginationModel?.pageSize ?? defaultPageSize;
  if (page > 0) {
    params[buildPrefixedKey(urlStateKey, "page")] = String(page);
  }
  if (pageSize !== defaultPageSize) {
    params[buildPrefixedKey(urlStateKey, "pageSize")] = String(pageSize);
  }

  const visibilityModel = state.columnVisibilityModel ?? {};
  const fieldsToCheck = new Set([
    ...(allowedFields ?? []),
    ...Object.keys(defaultColumnVisibility),
    ...Object.keys(visibilityModel),
  ]);

  for (const field of fieldsToCheck) {
    if (!isAllowedField(field, allowedSet)) {
      continue;
    }
    const currentVisible = isColumnVisible(field, visibilityModel);
    const defaultVisible = isColumnVisibleByDefault(field, defaultColumnVisibility);
    if (currentVisible === defaultVisible) {
      continue;
    }
    params[buildPrefixedKey(urlStateKey, "col", field)] = currentVisible ? "visible" : "hidden";
  }

  return params;
}
